import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import View from '@picsart/view/View';

import useMemoSelector from 'hooks/useMemoSelector';
import { getCropAlign, getCropOrientation, getCropTemplate, getCropRotate, getCropAttrs } from 'store/batch/selectors';
import batchSlice from 'store/batch';
import { cropOrientationValues } from 'pages/Batch/configs';
import { getResizingParams } from 'pages/Batch/utils';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { ICropPatch } from 'types';

import CropImage from '../CropImage';

import useStyles from './styles';

interface ICropWrapper {
  imgSizes: number[];
  itemKey: string;
}

const CropWrapper = ({ imgSizes, itemKey }: ICropWrapper) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { cropAlign, cropOrientation, cropTemplate, cropRotate, cropAttrs } = useMemoSelector(state => ({
    cropAlign: getCropAlign(state),
    cropOrientation: getCropOrientation(state),
    cropTemplate: getCropTemplate(state),
    cropRotate: getCropRotate(state),
    cropAttrs: getCropAttrs(state),
  }));

  const { isWeb } = useBatchData();

  const [imgWidth, imgHeight] = imgSizes;

  const defaultCropSizes = useMemo(() => ({
    x: 0,
    y: 0,
    width: imgWidth,
    height: imgHeight,
  }), [imgWidth, imgHeight]);

  const [cropSizes, setCropSizes] = useState<ICropPatch>(defaultCropSizes);
  const [cropInit, setCropInit] = useState({
    width: imgWidth,
    height: imgHeight,
  });

  const cropAttr = useMemo(() => cropAttrs.find(attr => attr.key === itemKey), [cropAttrs, itemKey]);

  useEffect(() => {
    if (cropAttr?.fireUpdate) {
      setCropSizes(cropAttr);
    }
  }, [cropAttr]);

  useEffect(() => {
    setCropInit({
      width: imgWidth,
      height: imgHeight,
    });
  }, [imgWidth, imgHeight]);

  useEffect(() => {
    dispatch(batchSlice.actions.updateCropAttrs({
      key: itemKey,
      ...cropSizes,
      initWidth: imgWidth,
      initHeight: imgHeight,
    }));
  }, [cropSizes, dispatch, itemKey, imgWidth, imgHeight]);

  useEffect(() => {
    const left = imgWidth - cropSizes.width;
    const top = imgHeight - cropSizes.height;
    const currentX = cropAlign === 'alignRight' ? left : cropAlign === 'alignHorizontal' ? left / 2 : 0;
    const currentY = cropAlign === 'alignBottom' ? top : cropAlign === 'alignVertical' ? top / 2 : 0;

    setCropSizes(prev => ({
      ...prev,
      x: ['alignRight', 'alignHorizontal', 'alignLeft'].includes(cropAlign || '') ? currentX : prev?.x,
      y: ['alignBottom', 'alignVertical', 'alignTop'].includes(cropAlign || '') ? currentY : prev?.y,
    }));
  }, [cropAlign, cropSizes?.width, cropSizes?.height, imgWidth, imgHeight]);

  useEffect(() => {
    if (!cropTemplate.sizes) {
      setCropSizes(defaultCropSizes);
      return;
    }
    const isPortrait = cropOrientation === cropOrientationValues[0];
    const minSize = Math.min(cropTemplate.sizes.width, cropTemplate.sizes.height);
    const maxSize = Math.max(cropTemplate.sizes.width, cropTemplate.sizes.height);
    const templateWidth = isPortrait ? minSize : maxSize;
    const templateHeight = isPortrait ? maxSize : minSize;
    const { fromX, fromY, width, height } = getResizingParams(templateWidth, templateHeight, imgWidth, imgHeight);
    width && height && setCropSizes({ x: fromX, y: fromY, width, height });
  }, [cropTemplate, imgWidth, imgHeight, cropOrientation, defaultCropSizes]);

  const isDrawCropImage = cropSizes?.width && cropSizes?.height && cropInit?.width && cropInit?.height;

  return (
    <View className={classes.cropWrap}>
      {isDrawCropImage && (
        <CropImage
          rotation={cropRotate}
          itemKey={itemKey}
          cropSizes={cropSizes}
          onChange={() => setCropSizes}
          objectWidth={cropInit.width}
          objectHeight={cropInit.height}
          isOptionsDraw={isWeb}
        />
      )}
    </View>
  );
};

export default memo(CropWrapper);
